import naja from "naja";
import {loadGoogleAPI} from "@weseus/cms-core/assets/js/googleHelpers";
import MarkerClusterer from "@googlemaps/markerclustererplus";
import noUiSlider from "nouislider";
import {debounce} from "@weseus/cms-core/assets/js/helpers";

export default class CategoryFiltersExtension {

	defaultPosition = {lat: 50.0760, lng: 14.444}; // Prague
	maxRadius = 100 * 1000; // 100km

	mapElem = null;
	placeInput = null;
	radiusInput = null;
	radiusSliderInput = null;

	map = null;
	placeAutocomplete = null;
	radiusSlider = null;

	constructor(naja) {
		naja.addEventListener('init', () => {
			this._initFiltering();
		});

		naja.addEventListener('success', (e) => {
			if (!e.options.mapUpdate && !e.response.productLikes) {
				this._updateMapData();
			}
		});
	}

	_initFiltering() {
		this.mapElem = document.getElementById('fn_products_map');
		this.placeInput = document.getElementById('fn_products_locality');
		this.radiusInput = document.getElementById('fn_products_locality_radius');
		this.radiusSliderInput = document.getElementById('fn_products_locality_radius_slider');

		this.map = new Map(this.mapElem, parseFloat(this.radiusInput.value) * 1000, this.maxRadius, this.defaultPosition);
		this.placeAutocomplete = new PlaceAutocomplete(this.placeInput, this.defaultPosition);
		this._initRadiusSlider(this.radiusSliderInput);

		this.placeAutocomplete.setPlaceChangedCallback((loc) => {
			const radius = parseFloat(this.radiusInput.value);
			this.map.setCircle(loc, radius * 1000);
			this._filterLocation(loc.lat(), loc.lng(), radius);
		});

		this.map.setLocationChangeCallback((loc = null, radius = 5000) => {
			this.placeAutocomplete.setOrigin(loc | this.defaultPosition);
			this.radiusInput.value = radius / 1000;
			this.radiusSlider.set(radius / 1000);

			this.placeInput.value = '';

			this._filterLocation(loc ? loc.lat() : '', loc ? loc.lng() : '', radius / 1000);
		});

		this.radiusInput.addEventListener('keyup', debounce((e) => {
			let radius = parseFloat(e.target.value);

			if (!radius) {
				radius = 10;
			}
			this.map.setCircleRadius(radius * 1000);

			this._filterLocation(this.map.getCirclePos().lat(), this.map.getCirclePos().lng(), radius);
		}));

		this.map.setLoadedCallback(() => {
			if (this.mapElem.hasAttribute('data-location')) {
				const locParts = this.mapElem.getAttribute('data-location').split(',');

				if (locParts.length !== 2) {
					return;
				}

				this.map.setCircle({
					lat: parseFloat(locParts[0]),
					lng: parseFloat(locParts[1]),
				}, locParts[2]);
			}

			this._updateMapData();
		});
	}

	_updateMapData() {
		if (this.mapElem.hasAttribute('data-products-location')) {
			const productsLocLink = this.mapElem.getAttribute('data-products-location');
			naja.makeRequest('GET', productsLocLink, null, {history: false, spinner: false, mapUpdate: true})
				.then((response) => {
					this.map.resetMarkers();
					const products = response.data;
					if (products) {
						this.map.addMarkers(products);
					}
				});
		}
	}

	_initRadiusSlider(slider) {
		this.radiusSlider = noUiSlider.create(slider, {
			start: [this.radiusInput.value],
			step: 1,
			range: {
				min: 1,
				max: 100
			},
		});

		this.radiusSlider.on('update', (values, handle) => {
			this.radiusInput.value = Math.round(values[handle]);
		});

		this.radiusSlider.on('change', (values, handle) => {
			this.map.setCircleRadius(values[handle] * 1000);
			this._filterLocation(this.map.getCirclePos().lat(), this.map.getCirclePos().lng(), values[handle]);
		});
	}

	_filterLocation(lat, lng, radius) {
		const link = this.mapElem.getAttribute('data-location-filter')
			.replace('---LAT---', lat)
			.replace('---LNG---', lng)
			.replace('---RADIUS---', radius);

		naja.makeRequest('POST', link, {spinner: false});
	}

}

class PlaceAutocomplete {

	input = null;

	defaultPosition = null;

	autocomplete = null;

	/**
	 * @type {null,function}
	 */
	placeChangedCallback = null;

	// Has user pressed the down key to navigate autocomplete options?
	hasDownBeenPressed = false;

	constructor(placeInput, defaultPos) {
		this.input = placeInput;
		this.defaultPosition = defaultPos;

		loadGoogleAPI(['places']).then(() => {
			this._init();
		});
	}

	setPlaceChangedCallback(callback) {
		this.placeChangedCallback = callback;
	}

	setOrigin(pos) {
		this.autocomplete.origin = pos;
	}

	_init() {
		let compRestrictions = {};
		if (this.input.hasAttribute('data-countries')) {
			compRestrictions.country = this.input.getAttribute('data-countries').split(',')
		}

		const options = {
			// bounds: defaultBounds,
			componentRestrictions: compRestrictions,
			fields: ["geometry"],
			origin: this.defaultPosition,
			strictBounds: false,
			types: ["(regions)"],
		};

		this.autocomplete = new google.maps.places.Autocomplete(this.input, options);

		// Listener outside to stop nested loop returning odd results
		this.input.addEventListener('keydown', (e) => {
			if (e.keyCode === 40) {
				this.hasDownBeenPressed = true;
			}
		});

		// GoogleMaps API custom eventlistener method
		google.maps.event.addDomListener(this.input, 'keydown', (e) => {

			// Maps API e.stopPropagation();
			e.cancelBubble = true;

			// If enter key, or tab key
			if (e.keyCode === 13 || e.keyCode === 9) {
				// If user isn't navigating using arrows and this hasn't ran yet
				if (!this.hasDownBeenPressed && !e.hasRanOnce) {
					try {
						google.maps.event.trigger(e.target, 'keydown', {
							keyCode: 40,
							hasRanOnce: true,
						});
					} catch (e) {} // ignore "a.stopPropagation is not a function"
				}
			}
		});

		// Clear the input on focus, reset hasDownBeenPressed
		this.input.addEventListener('focus', () => {
			this.hasDownBeenPressed = false;
			this.input.value = '';
		});

		this.autocomplete.addListener('place_changed', this._placeChanged.bind(this));
	}

	_placeChanged() {
		const place = this.autocomplete.getPlace();

		//If we can find the place lets go to it
		if (typeof place.address_components !== 'undefined') {
			// reset hasDownBeenPressed in case they don't unfocus
			this.hasDownBeenPressed = false;
		}

		if (!place.geometry) {
			return;
		}

		if (this.placeChangedCallback) {
			this.placeChangedCallback(place.geometry.location);
		}
	}

}

/**
 * @link https://www.w3schools.com/graphics/google_maps_reference.asp
 */
class Map {

	mapElement = null;

	markers = [];
	markerClusterer = null;
	detailWindows = [];
	errorWindow = null;

	circle = null;
	pos = null;
	radius = null;
	maxRadius = 100;

	/**
	 * @type {null,function}
	 */
	loadedCallback = null;

	/**
	 * @type {null,function}
	 */
	locationChangeCallback = null;

	constructor(mapElement, defaultRadius, maxRadius, defaultPos) {
		this.mapElement = mapElement;
		this.radius = defaultRadius;
		this.maxRadius = maxRadius;

		loadGoogleAPI(['places']).then(() => {
			this.pos = new google.maps.LatLng(defaultPos.lat, defaultPos.lng);
			this._init(mapElement);
		});
	}

	setLocationChangeCallback(callback) {
		this.locationChangeCallback = callback;
	}

	setLoadedCallback(callback) {
		this.loadedCallback = callback;
	}

	/**
	 * @param pos
	 * @param {int,null} radius
	 */
	setCircle(pos, radius= null) {
		this._showCircle(pos, radius);
	}

	/**
	 * @param {int} radius
	 */
	setCircleRadius(radius) {
		this._showCircle(this.pos, radius);
	}

	resetCircle() {
		this._hideCircle();
	}

	getCirclePos() {
		return this.pos;
	}

	setCenter(pos) {
		this.map.setCenter(pos);
	}

	addMarker(data) {
		const icon = {
			url: '/images/icons/svg/map-pointer.svg',
		};

		const marker = new google.maps.Marker({
			position: data.pos,
			map: this.map,
			title: data.name,
			icon,
		});

		marker.addListener("click", () => {
			this._showDetailWindow(data, marker);
		});

		this.markers.push(marker);
	}

	addMarkers(items) {
		items.forEach((item) => {
			this.addMarker(item);
		});

		if (!this.markerClusterer) {
			this.markerClusterer = new MarkerClusterer(this.map, this.markers, {
				maxZoom: 20,
				styles: [
					{
						width: 30,
						height: 30,
						className: "c_map__clustericon-1",
					},
					{
						width: 40,
						height: 40,
						className: "c_map__clustericon-2",
					},
					{
						width: 50,
						height: 50,
						className: "c_map__clustericon-3",
					},
				],
				clusterClass: 'c_map__clustericon',
			});

			this.markerClusterer.addListener("clusterclick", function (e) {
				const markers = e.getMarkers();

				//Check Markers array for duplicate position and offset a little
				if (markers .length > 1) {
					//Check if all markers are in the same position (with 4 significant digits)
					if (markers .every((val, index, arr) => (val.getPosition().lat().toFixed(4) == arr[0].getPosition().lat().toFixed(4)) && (val.getPosition().lng().toFixed(4) == arr[0].getPosition().lng().toFixed(4)))) {
						//Don't modify first element
						for (let i = 1; i < markers.length; i++) {
							const existingMarker = markers[i];
							const pos = existingMarker.getPosition();
							const quot = 360.0 / markers.length;
							const newLat = pos.lat() + -.00005 * Math.cos(+quot * i); //+ -.00005 * Math.cos((+quot * i) / 180 * Math.PI);  //x
							const newLng = pos.lng() + -.00005 * Math.sin(+quot * i);  //+ -.0005 * Math.sin((+quot * i) / 180 * Math.PI);  //Y
							existingMarker.setPosition(new google.maps.LatLng(newLat, newLng));
						}
					}
				}

				console.log(this.map.getZoom());
			});
		} else {
			this.markerClusterer.clearMarkers();
			this.markerClusterer.addMarkers(this.markers);

			// Zoom out to show the clusters buttons
			const zoom = this.map.getZoom();
			const maxZoom = this.markerClusterer.getMaxZoom();
			if (zoom >= maxZoom) {
				this.map.setZoom(maxZoom - 1);
			}
		}
	}

	resetMarkers() {
		this.markers.forEach((marker) => {
			marker.setMap(null);
		});
		this.markers = [];

		this.detailWindows.forEach((window) => {
			window.setMap(null);
		});
		this.detailWindows = [];
	}

	_init(mapElem) {
		this.map = new google.maps.Map(mapElem, {
			center: this.pos,
			zoom: 12,
			disableDefaultUI: true,
			zoomControl: true,
		});

		const collapse = mapElem.closest('.collapse');
		if (collapse) {
			const collapseBtn = document.querySelector(`[data-toggle="collapse"][data-target="#${collapse.id}"]`);
			if (collapseBtn) {
				collapseBtn.addEventListener('click', () => {
					this._initCurrentPosition();
				});
			} else {
				this._initCurrentPosition();
			}
		} else {
			this._initCurrentPosition();
		}

		this.map.addListener('center_changed', () => {
			if (this.circle === null) {
				this.pos = this.map.getCenter();
			}
		});

		// this.map.addListener('click', (e) => {
		// 	this._showCircle(e.latLng);
		//
		// 	this.locationChangeCallback(e.latLng, this.radius);
		// });

		this.loadedCallback();
	}

	_initCurrentPosition() {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const pos = {
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					};

					this.pos = pos;
					this.map.setCenter(pos);
					this.map.setZoom(12);
				},
				() => {
					//this._showErrorWindow("Error: The Geolocation service failed.");
				}
			);
		} else {
			// Browser doesn't support Geolocation
			//this._showErrorWindow("Error: Your browser doesn't support geolocation.");
		}
	}

	_showErrorWindow(content, pos = null) {
		if (this.errorWindow === null) {
			this.errorWindow = new google.maps.InfoWindow();
		}

		if (pos === null) {
			pos = this.map.getCenter()
		}

		this.errorWindow.setPosition(pos);
		this.errorWindow.setContent(content);
		this.errorWindow.open(this.map);
	}

	_showDetailWindow(data, marker) {
		const windowContent = `` +
			`<p class="h5"><a href="${data.url}">${data.name}</a></p>` +
			`<div>` +
			(data.image ? `<img src="${data.image}" alt="${data.name}" style="max-width: 100%; max-height: 200px;"><br>` : ``) +
			`<p>${data.content}</p>` +
			`<strong class="h5 d-inline-block">${data.price}</strong>` + (data.oldPrice ? `<small>${data.oldPrice}</small>` : ``) +
			`</div>`;

		let detailWindow = new google.maps.InfoWindow({
			content: windowContent,
		});

		detailWindow.open({
			anchor: marker,
			map: this.map,
			shouldFocus: false,
		});

		this.detailWindows.push(detailWindow);
	}

	_showCircle(pos, radius = null) {

		this.pos = pos;

		if (this.circle === null) {
			this.circle = new google.maps.Circle({
				strokeColor: "#176200",
				strokeOpacity: 0.5,
				strokeWeight: 2,
				fillColor: "#afafaf",
				fillOpacity: 0.35,
				map: this.map,
				draggable: true,
				center: this.pos,
				radius: this.radius
			});

			this.circle.addListener('dragend', (e) => {
				this.map.fitBounds(this.circle.getBounds());
				this.pos = e.latLng;
				this.locationChangeCallback(this.pos, this.radius);
			});

			document.addEventListener('keyup', (e) => {
				if (e.code === 'Escape') {
					this.circle.setMap(null);
					this.circle = null;
					this.locationChangeCallback();
				}
			});

		}

		this.circle.setCenter(pos);

		if (radius) {
			this.circle.setRadius(this._validateRadius(radius));
		}

		this.map.fitBounds(this.circle.getBounds());
	}

	_validateRadius(radius) {
		if (radius > this.maxRadius) {
			radius = this.maxRadius;
		}

		if (radius < 1) {
			radius = 1;
		}

		this.radius = radius;

		return radius;
	}

	_hideCircle() {
		if (this.circle !== null) {
			this.circle.setMap(null);
			this.circle = null;
		}
	}

}